import { useMemo } from 'react';
import { calcularProgressao, getStatusColor } from '@/utils/progressaoUtils';

export interface ResumoFuncionario {
  id: string;
  nome: string;
  carreira: string;
  nivelAtual: number;
  pontosAcumulados: number;
  pontosDescontados: number;
  pontosAtuaisCiclo: number;
  registos: {
    ano: number;
    avaliacao: string;
    pontuacaoChefe: number;
    pontuacaoCCA: number;
    pontos: number;
    fonte: 'siadap' | 'historico';
  }[];
}

interface FuncionarioResumoCardProps {
  resumo: ResumoFuncionario;
  pontosAtuaisCiclo: number;
  pontosSiadap: number;
  pontosHistorico: number;
  limiar: number;
  regraEspecial18Anos?: boolean;
  periodo: 'pre2025' | 'pos2025' | 'misto';
  pontosPre2025: number;
  pontosPos2025: number;
  onClick: () => void;
}

const avaliacaoConfig: Record<string, { bar: string; dot: string; label: string }> = {
  Excelente:   { bar: 'bg-emerald-400', dot: 'bg-emerald-500', label: 'Exc' },
  'Muito Bom': { bar: 'bg-teal-400',    dot: 'bg-teal-500',    label: 'MB'  },
  Bom:         { bar: 'bg-amber-400',   dot: 'bg-amber-500',   label: 'Bom' },
  Regular:     { bar: 'bg-orange-400',  dot: 'bg-orange-500',  label: 'Reg' },
  Inadequado:  { bar: 'bg-red-400',     dot: 'bg-red-500',     label: 'Inad'},
  Relevante:   { bar: 'bg-teal-300',    dot: 'bg-teal-400',    label: 'Rel' },
  Adequado:    { bar: 'bg-zinc-300',    dot: 'bg-zinc-400',    label: 'Adeq'},
};

const avatarPalette = [
  { bg: 'bg-rose-100',   text: 'text-rose-600'   },
  { bg: 'bg-violet-100', text: 'text-violet-600' },
  { bg: 'bg-amber-100',  text: 'text-amber-600'  },
  { bg: 'bg-teal-100',   text: 'text-teal-600'   },
  { bg: 'bg-cyan-100',   text: 'text-cyan-600'   },
  { bg: 'bg-orange-100', text: 'text-orange-600' },
];

function getInitials(nome: string) {
  return nome.split(' ').filter((_, i, arr) => i === 0 || i === arr.length - 1).map((n) => n[0]).join('');
}

function getAvatarPalette(id: string) {
  const idx = parseInt(id.replace(/\D/g, ''), 10) % avatarPalette.length;
  return avatarPalette[isNaN(idx) ? 0 : idx];
}

function fmt(n: number) {
  return n % 1 === 0 ? n.toFixed(0) : n.toFixed(1);
}

export default function FuncionarioResumoCard({
  resumo,
  pontosAtuaisCiclo,
  limiar,
  regraEspecial18Anos = false,
  onClick,
}: FuncionarioResumoCardProps) {
  const temProgressaoRegistada = (resumo.pontosDescontados ?? 0) > 0;
  const sorted = useMemo(() => [...resumo.registos].sort((a, b) => a.ano - b.ano), [resumo.registos]);
  const maxPontos = Math.max(...sorted.map((r) => r.pontos), 1);
  const palette = getAvatarPalette(resumo.id);
  const initials = getInitials(resumo.nome);

  const prog = calcularProgressao(pontosAtuaisCiclo, resumo.nivelAtual, limiar);
  const progColors = getStatusColor(prog.status);
  const isElegivel = prog.status === 'pronto' && !temProgressaoRegistada;

  const tendencia = useMemo(() => {
    if (sorted.length < 2) return 'neutro';
    const last = sorted[sorted.length - 1].pontos;
    const prev = sorted[sorted.length - 2].pontos;
    if (last > prev) return 'subida';
    if (last < prev) return 'descida';
    return 'neutro';
  }, [sorted]);

  const lastRegisto = sorted[sorted.length - 1];

  // Calcula média de pontos por avaliação
  const mediaPontos = sorted.length > 0
    ? sorted.reduce((s, r) => s + r.pontos, 0) / sorted.length
    : 0;

  // Melhor avaliação
  const melhorRegisto = sorted.length > 0
    ? sorted.reduce((best, r) => r.pontos > best.pontos ? r : best, sorted[0])
    : null;

  return (
    <div
      onClick={onClick}
      className="bg-white rounded-xl border border-zinc-100 hover:border-zinc-200 transition-all cursor-pointer group overflow-hidden"
    >
      {/* Status bar top */}
      <div className={`h-[3px] w-full ${isElegivel ? 'bg-emerald-500' : regraEspecial18Anos ? 'bg-amber-400' : 'bg-zinc-100'}`} />

      <div className="p-4">
        {/* Header */}
        <div className="flex items-start gap-3 mb-4">
          <div className={`w-10 h-10 flex items-center justify-center rounded-full text-[13px] font-bold flex-shrink-0 ${palette.bg} ${palette.text}`}>
            {initials}
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-[13px] font-semibold text-zinc-800 leading-tight truncate">{resumo.nome}</p>
            <p className="text-[11px] text-zinc-400 mt-0.5 truncate">{resumo.carreira}</p>
            <div className="flex items-center gap-1.5 mt-1 flex-wrap">
              <span className="inline-flex items-center gap-1 text-[10px] font-medium text-zinc-500 bg-zinc-50 border border-zinc-100 px-1.5 py-0.5 rounded-full">
                Nível {resumo.nivelAtual}
              </span>
              {regraEspecial18Anos && (
                <span className="inline-flex items-center gap-0.5 text-[10px] font-bold text-amber-700 bg-amber-50 border border-amber-200 px-1.5 py-0.5 rounded-full whitespace-nowrap">
                  <i className="ri-award-line text-[9px]" />18 anos
                </span>
              )}
              {isElegivel && (
                <span className="inline-flex items-center gap-0.5 text-[10px] font-bold text-emerald-700 bg-emerald-50 border border-emerald-200 px-1.5 py-0.5 rounded-full whitespace-nowrap">
                  <span className="w-1 h-1 rounded-full bg-emerald-500 animate-pulse inline-block" />
                  Elegível
                </span>
              )}
            </div>
          </div>
        </div>

        {/* Pontos destaque */}
        <div className="grid grid-cols-3 gap-2 mb-4">
          <div className={`text-center rounded-lg py-2.5 px-1 ${isElegivel ? 'bg-emerald-50 border border-emerald-100' : 'bg-zinc-50'}`}>
            <p className={`text-[20px] font-bold leading-tight ${isElegivel ? 'text-emerald-700' : 'text-zinc-900'}`}>{fmt(pontosAtuaisCiclo)}</p>
            <p className="text-[10px] text-zinc-400 leading-tight mt-0.5">pts ciclo</p>
          </div>
          <div className="text-center bg-zinc-50 rounded-lg py-2.5 px-1">
            <p className="text-[20px] font-bold text-zinc-900 leading-tight">{limiar}</p>
            <p className="text-[10px] text-zinc-400 leading-tight mt-0.5">limiar</p>
          </div>
          <div className="text-center bg-zinc-50 rounded-lg py-2.5 px-1">
            <p className="text-[20px] font-bold text-zinc-900 leading-tight">{sorted.length}</p>
            <p className="text-[10px] text-zinc-400 leading-tight mt-0.5">aval.</p>
          </div>
        </div>

        {/* Mini gráfico de barras melhorado */}
        {sorted.length > 0 ? (
          <div className="mb-4 bg-zinc-50 rounded-lg p-3">
            <div className="flex items-center justify-between mb-2">
              <span className="text-[10px] font-semibold text-zinc-400 uppercase tracking-widest">Evolução de pontos</span>
              <div className="flex items-center gap-1">
                {tendencia === 'subida' && (
                  <span className="flex items-center gap-0.5 text-[10px] text-emerald-600 font-semibold">
                    <i className="ri-arrow-up-line text-[10px]" />Subida
                  </span>
                )}
                {tendencia === 'descida' && (
                  <span className="flex items-center gap-0.5 text-[10px] text-red-500 font-semibold">
                    <i className="ri-arrow-down-line text-[10px]" />Descida
                  </span>
                )}
                {tendencia === 'neutro' && sorted.length >= 2 && (
                  <span className="flex items-center gap-0.5 text-[10px] text-zinc-400">
                    <i className="ri-subtract-line text-[10px]" />Estável
                  </span>
                )}
              </div>
            </div>
            {/* Barras com tooltip e ano */}
            <div className="flex items-end gap-1 h-10">
              {sorted.map((r, idx) => {
                const height = Math.max((r.pontos / maxPontos) * 100, 10);
                const cfg = avaliacaoConfig[r.avaliacao] ?? { bar: 'bg-zinc-300', dot: 'bg-zinc-400', label: '?' };
                const isLast = idx === sorted.length - 1;
                return (
                  <div
                    key={`${r.ano}-${r.fonte}-${idx}`}
                    className="flex-1 flex flex-col items-center justify-end gap-0.5"
                    title={`${r.ano}: ${fmt(r.pontos)} pts (${r.avaliacao})`}
                  >
                    <div
                      className={`w-full rounded-sm transition-all ${cfg.bar} ${isLast ? 'opacity-100' : 'opacity-70'}`}
                      style={{ height: `${height}%` }}
                    />
                  </div>
                );
              })}
            </div>
            {/* Anos */}
            <div className="flex items-center justify-between mt-1.5">
              <span className="text-[9px] text-zinc-300 font-mono">{sorted[0]?.ano}</span>
              {sorted.length > 2 && (
                <span className="text-[9px] text-zinc-300">···</span>
              )}
              <span className="text-[9px] text-zinc-300 font-mono">{sorted[sorted.length - 1]?.ano}</span>
            </div>
            {/* Média */}
            {sorted.length >= 2 && (
              <div className="flex items-center justify-between mt-2 pt-2 border-t border-zinc-100">
                <span className="text-[10px] text-zinc-400">Média por avaliação</span>
                <span className="text-[11px] font-bold text-zinc-600">{fmt(mediaPontos)} pts</span>
              </div>
            )}
          </div>
        ) : (
          <div className="mb-4 bg-zinc-50 rounded-lg h-16 flex items-center justify-center">
            <p className="text-[11px] text-zinc-300 italic">Sem avaliações registadas</p>
          </div>
        )}

        {/* Progressão bar */}
        <div className={`rounded-lg px-3 py-2.5 mb-3 ${isElegivel ? 'bg-emerald-50 border border-emerald-100' : 'bg-zinc-50'}`}>
          <div className="flex items-center justify-between mb-1.5">
            <span className={`text-[11px] font-semibold ${isElegivel ? 'text-emerald-700' : 'text-zinc-500'}`}>
              {isElegivel ? 'Elegível para progressão' : `${fmt(pontosAtuaisCiclo)} / ${limiar} pts`}
            </span>
            <span className={`text-[11px] font-bold ${isElegivel ? 'text-emerald-700' : 'text-zinc-400'}`}>
              {prog.percentagem.toFixed(0)}%
            </span>
          </div>
          <div className="h-1.5 bg-white rounded-full overflow-hidden">
            <div className={`h-full rounded-full transition-all ${progColors.bar}`} style={{ width: `${prog.percentagem}%` }} />
          </div>
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between pt-2.5 border-t border-zinc-100">
          {lastRegisto ? (
            <div className="flex items-center gap-1.5 min-w-0">
              <span className={`w-1.5 h-1.5 rounded-full flex-shrink-0 ${avaliacaoConfig[lastRegisto.avaliacao]?.dot ?? 'bg-zinc-300'}`} />
              <span className="text-[11px] text-zinc-400 truncate">
                <span className="font-medium text-zinc-600">{lastRegisto.avaliacao}</span>
                <span className="text-zinc-300 mx-1">·</span>
                {lastRegisto.ano}
              </span>
            </div>
          ) : (
            <span className="text-[11px] text-zinc-300 italic">Sem avaliações</span>
          )}
          {melhorRegisto && sorted.length >= 2 && (
            <span className="text-[10px] text-zinc-400 flex-shrink-0 ml-2">
              Melhor: <span className="font-semibold text-zinc-600">{fmt(melhorRegisto.pontos)} pts</span>
            </span>
          )}
        </div>
      </div>
    </div>
  );
}
